import React from 'react';
import { Box, Typography, Chip, Collapse, IconButton, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronDown as ExpandMoreIcon } from 'lucide-react';
import { ChevronUp as ExpandLessIcon } from 'lucide-react';
import WorkloadVulnerabilities from '@root/assets/svg/Kubernetes/WorkloadVulnerabilities';
import ContentBox from '@root/components/v2/content/ContentBox';

const DependencyNode = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  borderLeft: '2px solid #e0e0e0',
  paddingLeft: theme.spacing(1),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-2px',
    top: '12px',
    width: '8px',
    height: '2px',
    backgroundColor: '#e0e0e0',
  },
}));

const PackageBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  //   backgroundColor: isVulnerable ? '#ffebee' : '#f5f5f5',
  borderRadius: theme.spacing(0.5),
  border: '1px solid #e0e0e0',
}));

const DependencyTree = ({ dependencyData, component }) => {
  const [expandedNodes, setExpandedNodes] = React.useState(new Set());

  // Debug logging to understand data structure
  React.useEffect(() => {
    console.log('DependencyTree received data:', dependencyData);
  }, [dependencyData]);

  const toggleNode = nodeId => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderNode = (node, level = 0, parentId = null) => {
    const nodeId = `${parentId || 'root'}-${node.package_id || node.package_name}`;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(nodeId);

    return (
      <Box key={nodeId} sx={{ marginBottom: level === 0 ? 1 : 0.5 }}>
        <PackageBox isVulnerable={node.is_vulnerable}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {hasChildren && (
              <IconButton
                size="small"
                data-testid="click-k8s-pages-sideoverlay-dependencytree-65"
                data-clickable="true"
                onClick={() => toggleNode(nodeId)}
                sx={{ marginRight: 1, padding: 0.5 }}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
            {!hasChildren && <Box sx={{ width: 24 }} />}

            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {node.is_vulnerable && (
                  <WorkloadVulnerabilities style={{ color: '#d32f2f', marginRight: '10px', width: '14px' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '13px', fontFamily: 'Manrope' }}>
                  {node.package_name}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px', fontFamily: 'Manrope' }}>
                {node.package_version} ({node.package_type})
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {node.is_root && (
                <Chip
                  label="Root"
                  size="small"
                  sx={{ fontSize: '10px', height: '20px', fontFamily: 'Manrope' }}
                  color="primary"
                  variant="outlined"
                />
              )}
              {/* {node.is_vulnerable && (
                <Chip
                  label="Vulnerable"
                  size="small"
                  sx={{ fontSize: '10px', height: '20px', fontFamily: 'Manrope' }}
                  color="error"
                />
              )} */}
            </Box>
          </Box>
        </PackageBox>

        {hasChildren && (
          <Collapse in={isExpanded}>
            <DependencyNode>{node.children.map(child => renderNode(child, level + 1, nodeId))}</DependencyNode>
          </Collapse>
        )}
      </Box>
    );
  };

  // Check if component exists but no dependency tree data
  if (!dependencyData && component) {
    const componentParts = component.split('@');
    const packageName = componentParts[0] || component;
    const packageVersion = componentParts[1] || 'unknown';

    // Create a simple direct package display
    return (
      <Box sx={{ padding: 1 }}>
        <Typography variant="h6" sx={{ marginBottom: 2, fontSize: '14px', fontWeight: 'bold', fontFamily: 'Manrope' }}>
          Dependency Tree
        </Typography>
        {/* Only show the package as a tree node, not duplicate */}
        <Box sx={{ padding: 2, fontFamily: 'Manrope' }}>
          <PackageBox>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Box sx={{ width: 24 }} />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WorkloadVulnerabilities style={{ color: '#d32f2f', marginRight: '10px', width: '14px' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '13px', fontFamily: 'Manrope' }}>
                    {packageName}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px', fontFamily: 'Manrope' }}>
                  {packageVersion}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Chip
                  label="Direct Package"
                  size="small"
                  sx={{ fontSize: '10px', height: '20px', fontFamily: 'Manrope' }}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Box>
          </PackageBox>
        </Box>
      </Box>
    );
  }

  if (!dependencyData) {
    return (
      <Box sx={{ padding: 2, textAlign: 'center', fontFamily: 'Manrope' }}>
        <Typography variant="body2" color="text.secondary">
          No dependency tree data available
        </Typography>
      </Box>
    );
  }

  // Handle different data structures
  let trees = [];

  if (Array.isArray(dependencyData)) {
    // Direct array format
    trees = dependencyData;
    console.log('Using direct array format, trees:', trees);
  } else if (dependencyData.dependency_trees) {
    // Nested structure with dependency_trees array
    trees = dependencyData.dependency_trees;
    console.log('Using nested dependency_trees format, trees:', trees);
  } else if (dependencyData.package_name) {
    // Single tree object
    trees = [dependencyData];
    console.log('Using single tree object format, trees:', trees);
  } else {
    // Fallback - try to find any array property
    const arrayKeys = Object.keys(dependencyData).filter(key => Array.isArray(dependencyData[key]));
    if (arrayKeys.length > 0) {
      trees = dependencyData[arrayKeys[0]];
      console.log('Using fallback format, found array key:', arrayKeys[0], 'trees:', trees);
    } else {
      console.log('No valid tree data found in:', dependencyData);
    }
  }

  return (
    <Box sx={{ padding: 1 }}>
      <Typography variant="h6" sx={{ marginBottom: 2, fontSize: '14px', fontWeight: 'bold', fontFamily: 'Manrope' }}>
        Dependency Tree
      </Typography>

      {/* Display vulnerable package and main parents in same row */}
      <Grid container spacing={2} sx={{ marginBottom: 2, fontFamily: 'Manrope' }}>
        {/* Vulnerable Package */}
        <Grid item xs={6}>
          {dependencyData.vulnerable_package && (
            <ContentBox
              title="Vulnerable Package"
              titleSize="small"
              padding="12px"
              height="auto"
              titleContainerStyle={{
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#d32f2f',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
              containerStyle={{
                height: '100%',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 0.5 }}>
                <WorkloadVulnerabilities style={{ color: '#d32f2f', marginRight: '10px', width: '16px' }} />
                <Typography variant="body2" sx={{ fontSize: '12px', fontFamily: 'Manrope' }}>
                  {dependencyData.vulnerable_package.name} ({dependencyData.vulnerable_package.version})
                </Typography>
              </Box>
            </ContentBox>
          )}
        </Grid>

        {/* Main Parent Packages */}
        <Grid item xs={6}>
          {dependencyData.main_parents && dependencyData.main_parents.length > 0 && (
            <ContentBox
              title="Main Parent Packages"
              titleSize="small"
              padding="12px"
              height="auto"
              titleContainerStyle={{
                fontSize: '12px',
                fontWeight: 'bold',
              }}>
              {dependencyData.main_parents.map((parent, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ fontSize: '12px', marginBottom: 0.25, fontFamily: 'Manrope' }}>
                  • {parent.name} ({parent.version}) - {parent.type}
                </Typography>
              ))}
            </ContentBox>
          )}
        </Grid>
      </Grid>

      {/* Display trees side by side */}
      {trees.length > 0 ? (
        <Grid container spacing={2}>
          {trees.map((tree, treeIndex) => (
            <Grid item xs={6} key={treeIndex}>
              {renderNode(tree)}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ padding: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No dependency trees found in the data
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DependencyTree;
